import { getSessionToken } from './sessionTokenService';

export interface UpdateDefaultListRequest {
  defaultUserTickerListId: number;
}

export interface UpdateDefaultListResponse {
  message: string;
  defaultUserTickerListId: number;
  defaultTickerList: {
    id: number;
    listName: string;
    status: string;
  };
}

export interface GetDefaultListResponse {
  defaultUserTickerListId: number | null;
  defaultTickerList: {
    id: number;
    listName: string;
    status: string;
    createdAt: string;
    updatedAt: string;
  } | null;
}

export interface ClearDefaultListResponse {
  message: string;
  defaultUserTickerListId: null;
}

class UserApiService {
  private baseUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/v1/user`;

  private async makeRequest<T>(url: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No access token found');
    }

    // Add session token to all requests
    const sessionToken = getSessionToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers as Record<string, string>),
    };
    if (sessionToken) {
      headers['X-Session-Token'] = sessionToken;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Update user's default list
  async updateDefaultList(data: UpdateDefaultListRequest): Promise<UpdateDefaultListResponse> {
    return this.makeRequest<UpdateDefaultListResponse>(`${this.baseUrl}/default-list`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // Get user's default list
  async getDefaultList(): Promise<GetDefaultListResponse> {
    return this.makeRequest<GetDefaultListResponse>(`${this.baseUrl}/default-list`);
  }

  // Clear user's default list
  async clearDefaultList(): Promise<ClearDefaultListResponse> {
    return this.makeRequest<ClearDefaultListResponse>(`${this.baseUrl}/default-list`, {
      method: 'DELETE',
    });
  }
}

export const userApi = new UserApiService();
